import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useRouter } from "next/router";
import { VFC } from "react";
import styled from "styled-components";
import { useDisclosures } from "context/disclosures/useDisclosures";
import { DataTestId } from "types/enums/testId";
import { Markdown } from "./Markdown";

interface Props {
  className?: string;
  additionalItem?: string;
}

const partnerBrandedDisclosures = [
  `The information provided herein is general in nature and is for informational purposes only. It should not be used as a substitute for specific tax, legal and/or financial advice that considers all relevant facts and circumstances.  You are advised to consult a qualified financial adviser or tax professional before relying on the information provided herein. Deadlines and other program details are subject to change by the state without notice and should be checked prior to making any decisions.
`,
];

const Disclosure: VFC<Props> = withErrorBoundary(({ additionalItem = "", className = "" }) => {
  const { disclosuresInCorrectOrder } = useDisclosures();

  const { asPath } = useRouter();

  const isShowPartnerBrandedDisclosures = asPath.includes("statemandates/partners");

  return disclosuresInCorrectOrder.length ? (
    <StyledDisclosure className={className}>
      <div className="Content" data-testid={DataTestId.DisclosureContainer}>
        {isShowPartnerBrandedDisclosures && (
          <ul className="Disclosure__partnerBrandedDisclosures">
            {partnerBrandedDisclosures.map((item) => (
              <li key={item}>
                <Markdown className="Font_14_20_450 Color_fog40">{item}</Markdown>
              </li>
            ))}
          </ul>
        )}
        <ul className="Disclosure">
          {disclosuresInCorrectOrder.map((disclosure: any, index: number) => (
            <li className="Disclosure__disclosure Color_fog40" key={index}>
              <sup className="Font_14_20_450">{index + 1}</sup>
              {disclosure?.attributes.text ? (
                <Markdown className="Font_14_20_450 Color_fog40">{disclosure?.attributes.text}</Markdown>
              ) : (
                <p className="Disclosure__disclosureNotFound Color_fog40">
                  Please check data-disclosure-title in strapi
                </p>
              )}
            </li>
          ))}
          {additionalItem ? <li className="Disclosure__disclosure Font_14_20_450">{additionalItem}</li> : null}
        </ul>
      </div>
    </StyledDisclosure>
  ) : null;
}, {});

const StyledDisclosure = styled.div`
  padding-top: 80px;
  background: ${({ theme }) => theme.colors.fog["80"]};

  .Disclosure__partnerBrandedDisclosures {
    margin-bottom: 40px;
  }

  .Disclosure {
    padding-bottom: 40px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["60"]};
  }

  .Disclosure__disclosure {
    display: flex;
  }

  .Disclosure__disclosure:not(:first-child) {
    margin-top: 8px;
  }

  .Disclosure__disclosureNotFound {
    color: red;
  }

  sup {
    min-width: 8px;
    font-size: 75%;
    position: relative;
    margin-right: 16px;
  }

  a:hover {
    text-decoration: underline;
  }

  @media (max-width: 610px) {
    padding-top: 80px;
  }
`;

gql`
  fragment DisclosureContent on Disclosure {
    title
    text
  }
`;

export { Disclosure };
